/* R24 CRO: opt-in guest shortlist and navigation inside the existing product zoom.
   These tools never change the product form, variant selection, cart or prices. */
(() => {
  'use strict';
  const key = 'venlu-saved-pieces-r24-v1';
  const limit = 24;
  const copy = (de, en) => document.body.dataset.locale === 'de' ? de : en;
  const safeImage = value => {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const url = new URL(value, location.origin);
      if (url.protocol !== 'https:' || !(url.origin === location.origin || url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.shopifycdn.com'))) return '';
      if (url.origin === location.origin && !/^\/(cdn\/shop\/|s\/files\/)/.test(url.pathname)) return '';
      url.searchParams.set('width', '160');
      return url.href;
    } catch { return ''; }
  };
  const cleanItem = value => {
    if (!value || !Number.isSafeInteger(Number(value.id)) || Number(value.id) <= 0 || !/^[a-z0-9][a-z0-9-]{0,180}$/.test(value.handle || '')) return null;
    const image = safeImage(value.image);
    if (!image || typeof value.title !== 'string' || !value.title.trim()) return null;
    return { id: Number(value.id), handle: value.handle, title: value.title.slice(0, 240), variant: typeof value.variant === 'string' ? value.variant.slice(0, 160) : '', image };
  };
  const cleanList = value => {
    if (!Array.isArray(value)) return [];
    const seen = new Set();
    return value.map(cleanItem).filter(item => item && !seen.has(item.id) && seen.add(item.id)).slice(0, limit);
  };
  const productUrl = item => {
    const root = new URL(document.body.dataset.root || '/', location.origin);
    const path = root.origin === location.origin ? root.pathname.replace(/\/?$/, '/') : '/';
    return path + 'products/' + item.handle + '?variant=' + item.id;
  };
  const galleryIndex = (index, delta, length) => Math.max(0, Math.min(length - 1, index + delta));
  window.VenluPdpToolsR24 = { cleanItem, cleanList, productUrl, galleryIndex };

  class ProductSave extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.section = this.closest('[data-purchase-product]');
      this.select = this.section?.querySelector('#vn-variant');
      try {
        this.product = JSON.parse(this.querySelector('[data-save-product]').textContent);
        this.variants = JSON.parse(this.section.querySelector('[data-variants]').textContent);
        this.images = JSON.parse(this.section.querySelector('[data-variant-images]')?.textContent || '{}');
      } catch { return; }
      if (!this.select || !Array.isArray(this.variants)) return;
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.items = this.read();
      this.button = this.querySelector('[data-save-piece]');
      this.status = this.querySelector('[data-save-status]');
      this.button.addEventListener('click', () => this.toggle(), { signal });
      this.querySelector('[data-saved-list]').addEventListener('click', event => {
        const button = event.target.closest('[data-remove-saved]');
        if (!button) return;
        const index = this.items.findIndex(item => item.id === Number(button.dataset.removeSaved));
        const next = this.items.filter(item => item.id !== Number(button.dataset.removeSaved));
        if (!this.persist(next)) return;
        this.items = next;
        this.status.textContent = window.VenluI18n.t("r24.s_ed3ff2ed86b3",{},"Removed from saved pieces.");
        this.render();
        const controls = [...this.querySelectorAll('[data-remove-saved]')];
        (controls[Math.min(index, controls.length - 1)] || this.querySelector('summary')).focus({ preventScroll: true });
      }, { signal });
      // The purchase controller updates option state first; this observer only reads it.
      this.section.addEventListener('click', event => {
        if (event.target.closest('.variant-buttons [data-value]')) queueMicrotask(() => this.renderButton());
      }, { signal });
      this.select.addEventListener('change', () => queueMicrotask(() => this.renderButton()), { signal });
      document.addEventListener('venlu:languagechange', () => this.render(), { signal });
      window.addEventListener('storage', event => { if (event.key === key || event.key === null) { this.items = this.read(); this.render(); } }, { signal });
      this.hidden = false;
      this.render();
    }
    disconnectedCallback() { this.abort?.abort(); this.abort = null; }
    read() { try { return cleanList(JSON.parse(localStorage.getItem(key) || '[]')); } catch { return []; } }
    persist(items) {
      try { if (items.length) localStorage.setItem(key, JSON.stringify(items)); else localStorage.removeItem(key); return true; }
      catch { this.status.textContent = window.VenluI18n.t("r24.s_fc9a530913d8",{},"Your browser cannot save this list right now. You can bookmark the product page instead."); return false; }
    }
    current() {
      const variant = this.variants.find(item => String(item.id) === this.select.value);
      if (!variant) return null;
      return cleanItem({ id: variant.id, handle: this.product.handle, title: this.product.title, variant: variant.title === 'Default Title' ? '' : variant.title, image: this.images[variant.id] || this.product.image });
    }
    toggle() {
      const current = this.current();
      if (!current) return;
      const latest = this.read();
      const exists = latest.some(item => item.id === current.id);
      if (!exists && latest.length >= limit) {
        this.querySelector('[data-saved-details]').open = true;
        this.status.textContent = window.VenluI18n.t("r24.s_bc3774380bdb",{},"You have saved 24 pieces. Remove one before saving this piece.");
        return;
      }
      const next = exists ? latest.filter(item => item.id !== current.id) : [current, ...latest];
      if (!this.persist(next)) return;
      this.items = next;
      this.status.textContent = exists ? window.VenluI18n.t("r24.s_ed3ff2ed86b3",{},"Removed from saved pieces.") : window.VenluI18n.t("r24.s_f8d1b997a83b",{},"Saved with your selected variant in this browser.");
      this.render();
    }
    renderButton() {
      const current = this.current();
      const saved = Boolean(current && this.items.some(item => item.id === current.id));
      this.button.disabled = !current;
      this.button.setAttribute('aria-pressed', String(saved));
      this.querySelector('[data-save-label]').textContent = saved ? window.VenluI18n.t("r24.s_b5c120b316c2",{},"Saved") : window.VenluI18n.t("r24.s_a528c8f0f0d9",{},"Save piece");
    }
    render() {
      this.renderButton();
      this.querySelector('[data-saved-count]').textContent = String(this.items.length);
      this.querySelector('[data-saved-empty]').hidden = this.items.length > 0;
      const list = this.querySelector('[data-saved-list]');
      list.replaceChildren();
      this.items.forEach(item => {
        const li = document.createElement('li'); li.className = 'r24-product-save__item';
        const image = document.createElement('img'); image.src = item.image; image.alt = ''; image.width = 58; image.height = 66; image.loading = 'lazy';
        const link = document.createElement('a'); link.href = productUrl(item); link.textContent = item.title;
        if (item.variant) { const meta = document.createElement('small'); meta.textContent = item.variant; link.append(meta); }
        const button = document.createElement('button'); button.type = 'button'; button.dataset.removeSaved = item.id;
        button.setAttribute('aria-label', window.VenluI18n.t("r24.s_dd0681e205d3",{},"Remove: ") + item.title + (item.variant ? ', ' + item.variant : ''));
        // Fixed functional icon; stored product strings enter the DOM only through textContent.
        button.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg>';
        li.append(image, link, button); list.append(li);
      });
    }
  }
  if (!customElements.get('r24-product-save')) customElements.define('r24-product-save', ProductSave);

  // Fit the modal to the actual decoded image ratio, not a fixed wide canvas.
  // Narrow/short viewports may scroll; product edges are never cropped.
  const zoomFrameWidth = (imageWidth, imageHeight, viewportWidth, viewportHeight, horizontalChrome, verticalChrome) => {
    if (![imageWidth, imageHeight, viewportWidth, viewportHeight].every(value => Number.isFinite(value) && value > 0)) return null;
    const availableWidth = Math.max(1, viewportWidth - 24);
    const imageSpace = Math.max(120, viewportHeight - 24 - verticalChrome);
    const minWidth = Math.min(320, availableWidth);
    return Math.floor(Math.min(availableWidth, 1000, Math.max(minWidth, Math.min(imageWidth, imageSpace * imageWidth / imageHeight) + horizontalChrome)));
  };
  function initZoomFrame(section) {
    const dialog = section.querySelector('.vn-product-zoom');
    const image = dialog?.querySelector('[data-zoom-image]');
    if (!dialog || !image || dialog.dataset.zoomFrameReady) return;
    dialog.dataset.zoomFrameReady = 'true';
    dialog.classList.add('r32-zoom-fitted');
    const abort = new AbortController(), { signal } = abort;
    const nav = dialog.querySelector('[data-cro-gallery]');
    let frame = 0;
    const fit = () => {
      frame = 0;
      if (!dialog.open || !dialog.isConnected || !image.complete || !image.naturalWidth || !image.naturalHeight) return;
      const style = getComputedStyle(dialog);
      const number = value => parseFloat(value) || 0;
      const horizontalChrome = number(style.paddingLeft) + number(style.paddingRight) + number(style.borderLeftWidth) + number(style.borderRightWidth);
      const navStyle = nav && !nav.hidden ? getComputedStyle(nav) : null;
      const verticalChrome = number(style.paddingTop) + number(style.paddingBottom) + number(style.borderTopWidth) + number(style.borderBottomWidth) + (navStyle ? nav.offsetHeight + number(navStyle.marginTop) + number(navStyle.marginBottom) : 0);
      const viewportWidth = Math.min(document.documentElement.clientWidth || innerWidth, window.visualViewport?.width || innerWidth);
      const viewportHeight = Math.min(document.documentElement.clientHeight || innerHeight, window.visualViewport?.height || innerHeight);
      const width = zoomFrameWidth(image.naturalWidth, image.naturalHeight, viewportWidth, viewportHeight, horizontalChrome, verticalChrome);
      if (width !== null && dialog.style.getPropertyValue('--r32-zoom-width') !== width + 'px') dialog.style.setProperty('--r32-zoom-width', width + 'px');
    };
    const schedule = () => { if (dialog.open && !frame) frame = requestAnimationFrame(fit); };
    image.addEventListener('load', schedule, { signal });
    window.addEventListener('resize', schedule, { signal });
    window.visualViewport?.addEventListener('resize', schedule, { signal });
    const observer = new MutationObserver(schedule);
    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    const resize = typeof ResizeObserver === 'function' && nav ? new ResizeObserver(schedule) : null;
    if (resize) resize.observe(nav);
    section.addEventListener('pointerdown', event => { section.dataset.galleryPointer = String(event.pointerType === 'mouse'); }, { signal });
    document.addEventListener('keydown', () => { delete section.dataset.galleryPointer; }, { signal });
    document.addEventListener('shopify:section:unload', event => {
      if (!event.target.contains(section)) return;
      abort.abort(); observer.disconnect(); resize?.disconnect();
      if (frame) cancelAnimationFrame(frame);
      delete dialog.dataset.zoomFrameReady; delete section.dataset.galleryPointer;
    }, { signal });
    schedule();
  }

  // Mobile gallery: a deliberate tap on either half, never a swipe.
  function initGalleryTap(section) {
    const surface = section.querySelector('.gallery-main'), image = surface?.querySelector('#vn-main-image');
    const thumbs = [...section.querySelectorAll('.gallery-thumbs [data-image]')];
    if (!surface || !image || thumbs.length < 2 || surface.dataset.tapReady) return;
    surface.dataset.tapReady = 'true';
    const abort = new AbortController(), { signal } = abort;
    const mobile = matchMedia('(max-width:767px)'), reduced = matchMedia('(prefers-reduced-motion:reduce)');
    let gesture = null, dragged = false, animation = null;
    surface.addEventListener('pointerdown', event => {
      if (!mobile.matches || event.target.closest('button,a,input')) return;
      if (!event.isPrimary) { dragged = true; return; }
      gesture = { id: event.pointerId, x: event.clientX, y: event.clientY }; dragged = false;
    }, { signal, passive:true });
    surface.addEventListener('pointermove', event => {
      if (gesture && event.pointerId === gesture.id && Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > 10) dragged = true;
    }, { signal, passive:true });
    surface.addEventListener('pointercancel', () => { dragged = true; gesture = null; }, { signal, passive:true });
    surface.addEventListener('click', event => {
      if (!mobile.matches || event.target.closest('button,a,input') || event.detail === 0) return;
      event.preventDefault(); event.stopPropagation();
      if (dragged) { gesture = null; return; }
      const bounds = surface.getBoundingClientRect(), step = event.clientX < bounds.left + bounds.width / 2 ? -1 : 1;
      const current = Math.max(0, thumbs.findIndex(thumb => thumb.getAttribute('aria-current') === 'true'));
      const next = (current + step + thumbs.length) % thumbs.length;
      thumbs[next].click(); gesture = null;
      const rail = thumbs[next].parentElement, item = thumbs[next].getBoundingClientRect(), rb = rail.getBoundingClientRect();
      if (item.left < rb.left || item.right > rb.right) rail.scrollTo({ left:rail.scrollLeft + item.left - rb.left - (rail.clientWidth - item.width) / 2, behavior:reduced.matches ? 'instant' : 'smooth' });
      animation?.cancel();
      if (!reduced.matches) animation = image.animate([{opacity:.65},{opacity:1}],{duration:160,easing:'ease-out'});
    }, { signal, capture:true });
    document.addEventListener('shopify:section:unload', event => {
      if (!event.target.contains(section)) return;
      abort.abort(); animation?.cancel(); delete surface.dataset.tapReady;
    }, { signal });
  }

  function initGallery(section) {
    const dialog = section.querySelector('.vn-product-zoom');
    const nav = dialog?.querySelector('[data-cro-gallery]');
    const original = [...section.querySelectorAll('.gallery-thumbs [data-image]')];
    const main = section.querySelector('#vn-main-image');
    if (!dialog || !nav || !main || original.length < 2 || nav.dataset.ready) return;
    nav.dataset.ready = 'true';
    const abort = new AbortController(), { signal } = abort;
    const thumbList = nav.querySelector('[data-cro-gallery-thumbs]');
    let index = 0, built = false;
    const buttons = () => [...thumbList.querySelectorAll('button')];
    const sync = () => {
      if (!dialog.open) return;
      if (!built) {
        original.forEach((source, i) => {
          const button = document.createElement('button'); button.type = 'button'; button.dataset.croImage = String(i);
          const image = source.querySelector('img')?.cloneNode(true);
          if (image) { image.removeAttribute('id'); image.alt = ''; button.append(image); }
          thumbList.append(button);
        });
        built = true; nav.hidden = false; dialog.classList.add('r24-gallery-expanded');
      }
      const active = original.findIndex(thumb => thumb.getAttribute('aria-current') === 'true');
      if (active >= 0) index = active;
      dialog.querySelector('[data-zoom-image]').src = main.src;
      dialog.querySelector('[data-zoom-image]').alt = main.alt;
      nav.querySelector('[data-cro-gallery-count]').textContent = window.VenluI18n.t("r24.s_0413980726f1",{},"Image ") + (index + 1) + ' / ' + original.length;
      buttons().forEach((button, i) => {
        button.setAttribute('aria-label', original[i].getAttribute('aria-label') || window.VenluI18n.t("r24.s_d5e0fc3e50ff",{},"Product image ") + (i + 1));
        if (i === index) button.setAttribute('aria-current', 'true'); else button.removeAttribute('aria-current');
      });
      nav.querySelector('[data-cro-gallery-step="-1"]').disabled = index === 0;
      nav.querySelector('[data-cro-gallery-step="1"]').disabled = index === original.length - 1;
    };
    const select = next => {
      index = galleryIndex(next, 0, original.length);
      original[index].click(); // Existing gallery controller owns main image and current thumbnail.
      sync();
      buttons()[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    };
    nav.addEventListener('click', event => {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.dataset.croImage !== undefined) select(Number(button.dataset.croImage));
      else if (button.dataset.croGalleryStep) select(galleryIndex(index, Number(button.dataset.croGalleryStep), original.length));
    }, { signal });
    dialog.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      select(event.key === 'Home' ? 0 : event.key === 'End' ? original.length - 1 : galleryIndex(index, event.key === 'ArrowRight' ? 1 : -1, original.length));
    }, { signal });
    const observer = new MutationObserver(sync);
    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    observer.observe(main, { attributes: true, attributeFilter: ['src'] });
    document.addEventListener('venlu:languagechange', sync, { signal });
    document.addEventListener('shopify:section:unload', event => {
      if (!event.target.contains(section)) return;
      observer.disconnect(); abort.abort(); delete nav.dataset.ready;
    }, { signal });
  }
  const init = (scope = document) => scope.querySelectorAll('[data-purchase-product]').forEach(section => { initZoomFrame(section); initGallery(section); initGalleryTap(section); });
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
